'use client';
/**
 * ./src/modules/product/components/samples/SamplesInspirationCard.tsx
 *
 * SamplesInspirationCard component
 *
 * @copyright DesignShop, 2024
 * @since 06/17/2024
 */
import { Button } from '@/modules/common/components/Button';
import Image from '@/modules/common/components/Image';
import Link from '@/modules/common/components/ui/Link';
import useIsMobile from '@/modules/common/hooks/useIsMobile';
import { cn } from '@/modules/common/lib/utils';
import { selectContentDataLayerEvent } from '@/modules/product/services/data-layer-events/select_content';

export interface SampleInspirationCardProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  thumbnail?: string;
  cta: string;
  url?: string;
  testid?: string;
  mobileWidth?: number;
  mobileHeight?: number;
  desktopHeight?: number;
  isMobile?: boolean;
}

export const SamplesInspirationCard = ({
  className,
  thumbnail,
  cta,
  url,
  testid,
  mobileWidth = 315,
  mobileHeight = 202,
  desktopHeight = 220,
}: SampleInspirationCardProps) => {
  const isMobile = useIsMobile();

  return (
    <Link
      href={url!}
      className={cn(
        'relative flex flex-col items-center justify-center overflow-hidden rounded-xxl bg-gray-100 px-6 py-4',
        className
      )}
      style={{
        minWidth: isMobile ? `${mobileWidth}px` : '100%',
        minHeight: `${isMobile ? mobileHeight : desktopHeight}px`,
      }}
      data-testid={testid || 'samples-inspiration-card-link'}
      onClick={() =>
        selectContentDataLayerEvent({
          contentType: 'Quick link',
          name: cta || '',
        })
      }
    >
      {thumbnail && (
        <div className="absolute inset-0 transition-transform duration-700 hover:scale-110">
          <Image
            src={thumbnail}
            alt="Collection Banner"
            className="h-full w-full rounded-xxl object-cover mix-blend-darken"
            data-testid="samples-inspiration-card-image"
          />
        </div>
      )}

      <div className="relative z-10">
        <Button
          variant="secondary"
          className="px-4 py-3"
          data-testid="samples-inspiration-card-button"
        >
          {cta}
        </Button>
      </div>
    </Link>
  );
};
